/**
 * Marriage Certificate Detail Page
 * Displays detailed information for printing and finalizing marriage certificate applications
 * Used for /marriages/print/:id and /marriages/finalize/:id routes
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { BASE_URL } from "../config/api";
import { certificateService } from "../services/api/certificate.service";
import { applicantService } from "../services/api/applicant.service";
import { paymentService } from "../services/api/payment.service";
import { officiatorService } from "../services/api/officiator.service";
import { settingsService } from "../services/api/settings.service";
import { workflowService } from "../services/api/workflow.service";
import type { QueryWorkflowActionDto } from "../types/workflow.types";
import type {
  CertificateApplication,
  UpdateCertificateApplicationDto,
  County,
  Nationality,
  AuthoritySignature,
  SignedCertificate,
} from "../types/certificate.types";
import type {
  Applicant,
  CreateApplicantDto,
  UpdateApplicantDto,
} from "../types/applicant.types";
import type {
  Officiator,
  CreateOfficiatorDto,
  UpdateOfficiatorDto,
} from "../types/officiator.types";
import type { Attachment } from "../types/attachment.types";

import { ApplicationCategory } from "../types/certificate.types";
import { ApplicantType } from "../types/applicant.types";
import { UserStatusId } from "../types/user.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import {
  showSuccessToast,
  showErrorToast,
  showDeleteConfirm,
  showConfirm,
} from "../utils/toast";
import {
  GENERIC_ERROR_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from "../utils/constants";
import { sanitizePhoneNumberInput, validatePhoneNumber } from "../utils/phone";
import "../styles/pages/UserDetail.css";
import "../styles/pages/Users.css";
import { CardPayment } from "./CardPayment";
import { ManualPayment } from "./ManualPayment";
import { DocumentScan } from "./DocumentScan";
import { SignatureExtraction } from "./SignatureExtraction";
import { CountyMarriageCert } from "../components/CountyMarriageCert";
import QueryApplicationForm from "../components/common/QueryApplicationForm";
import { apiRequest } from "../api/apiClient";
import { getFullPath } from "../utils/getFullPath";

// Default nationality constant
const DefaultNationality = "Liberian";
const BasicRole = 2;

// Calculate max date for 18+ age requirement (18 years before current date)
const getMaxDateFor18Plus = (): string => {
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );
  return eighteenYearsAgo.toISOString().split("T")[0];
};

export const MarriageCertDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Determine back navigation path based on current URL
  const getBackPath = () => {
    if (location.pathname.includes("/print/")) {
      return "/marriages/print";
    }
    if (location.pathname.includes("/finalize/")) {
      return "/marriages/finalize";
    }
    return "/marriages/apply";
  };

  // Check if we're on the print page
  const isPrintPage = location.pathname.includes("/print/");

  // Check if we're on the finalize page
  const isFinalizePage = location.pathname.includes("/finalize/");

  const [application, setApplication] = useState<CertificateApplication | null>(
    null,
  );
  const [counties, setCounties] = useState<County[]>([]);
  const [nationalities, setNationalities] = useState<Nationality[]>([]);
  const [ministryName, setMinistryName] = useState<string>(
    "Ministry of Internal Affairs",
  );
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  // Workflow approval state
  const [isAtMyApprovalLevel, setIsAtMyApprovalLevel] = useState(false);
  const [currentStageOrder, setCurrentStageOrder] = useState(0);

  // Receive Signed Cert modal state
  const [showSignedCertModal, setShowSignedCertModal] = useState(false);

  // Signed certificate state
  const [signedCertificate, setSignedCertificate] =
    useState<SignedCertificate | null>(null);
  const [signedCertLoading, setSignedCertLoading] = useState(false);

  // Signature extraction modal state
  const [showSignatureExtractionModal, setShowSignatureExtractionModal] =
    useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);

  // Authority signatures state
  const [, setAuthoritySignatures] = useState<AuthoritySignature[]>([]);
  const [, setAuthoritySignaturesLoading] = useState(false);

  // Certificate signatures state (for signature extraction)
  const [certificateSignatures, setCertificateSignatures] = useState<any[]>([]);
  const [, setCertificateSignaturesLoading] = useState(false);

  // Accordion state
  const [groomAccordionOpen, setGroomAccordionOpen] = useState(false);
  const [brideAccordionOpen, setBrideAccordionOpen] = useState(false);
  const [officiatorAccordionOpen, setOfficiatorAccordionOpen] = useState(false);
  const [attachmentsAccordionOpen, setAttachmentsAccordionOpen] =
    useState(false);

  // Payment dropdown state
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [hasExistingPayment, setHasExistingPayment] = useState(false);

  // Submission readiness state
  const [isReadyForSubmission, setIsReadyForSubmission] = useState(false);

  // Manual payment modal state
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  // Card payment (Stripe) modal state
  const [showCardPaymentModal, setShowCardPaymentModal] = useState(false);

  // Document scan modal state
  const [showDocumentScanModal, setShowDocumentScanModal] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [viewingAttachmentId, setViewingAttachmentId] = useState<number | null>(
    null,
  );

  // Groom applicant state
  const [groomApplicant, setGroomApplicant] = useState<Applicant | null>(null);
  const [groomFormData, setGroomFormData] = useState<CreateApplicantDto>({
    userId: undefined,
    firstName: "",
    middleName: "",
    lastName: "",
    genderId: 1,
    dateOfBirth: "",
    nationalIdNumber: "",
    email: "",
    primaryPhone: "",
    secondaryPhone: "",
    certificateApplicationId: 0,
    applicantType: "Groom",
    countyOfOriginId: 0,
    nationalityId: 0,
    birthCountyId: 0,
    birthTown: "",
    currentAddress: "",
    wasPreviouslyMarried: false,
    previousMarriageDate: "",
    previousSpouseName: "",
    fatherName: "",
    motherName: "",
  });
  const [groomLoading, setGroomLoading] = useState(false);
  const [groomError, setGroomError] = useState("");
  const [groomValidationErrors, setGroomValidationErrors] = useState<
    Record<string, string>
  >({});
  const [groomPhotoFile, setGroomPhotoFile] = useState<File | null>(null);
  const [groomPhotoPreview, setGroomPhotoPreview] = useState<string | null>(
    null,
  );

  // Bride applicant state
  const [brideApplicant, setBrideApplicant] = useState<Applicant | null>(null);
  const [brideFormData, setBrideFormData] = useState<CreateApplicantDto>({
    userId: undefined,
    firstName: "",
    middleName: "",
    lastName: "",
    genderId: 2,
    dateOfBirth: "",
    nationalIdNumber: "",
    email: "",
    primaryPhone: "",
    secondaryPhone: "",
    certificateApplicationId: 0,
    applicantType: "Bride",
    countyOfOriginId: 0,
    nationalityId: 0,
    birthCountyId: 0,
    birthTown: "",
    currentAddress: "",
    wasPreviouslyMarried: false,
    previousMarriageDate: "",
    previousSpouseName: "",
    fatherName: "",
    motherName: "",
    brideProposedName: "",
  });
  const [brideLoading, setBrideLoading] = useState(false);
  const [brideError, setBrideError] = useState("");
  const [brideValidationErrors, setBrideValidationErrors] = useState<
    Record<string, string>
  >({});
  const [bridePhotoFile, setBridePhotoFile] = useState<File | null>(null);
  const [bridePhotoPreview, setBridePhotoPreview] = useState<string | null>(
    null,
  );

  // Officiator state
  const [officiator, setOfficiator] = useState<Officiator | null>(null);
  const [officiatorFormData, setOfficiatorFormData] =
    useState<CreateOfficiatorDto>({
      certificateApplicationId: 0,
      officiatorName: "",
      officiatorContact: "",
      witnessName: "",
      witnessContact: "",
    });
  const [officiatorLoading, setOfficiatorLoading] = useState(false);
  const [officiatorError, setOfficiatorError] = useState("");
  const [officiatorValidationErrors, setOfficiatorValidationErrors] = useState<
    Record<string, string>
  >({});

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] =
    useState<UpdateCertificateApplicationDto>({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Set page title based on page type
    if (isPrintPage) {
      setPageTitle("Print Certificate", "Print marriage certificate");
    } else if (isFinalizePage) {
      setPageTitle("Finalize Certificate", "Finalize and sign certificate");
    } else {
      setPageTitle("Application Detail", "View application information");
    }

    fetchData();
    fetchCounties();
    fetchNationalities();
    fetchSettings();
    if (id) {
      fetchApplicants();
      fetchOfficiator();
      fetchAttachments();
      if (isFinalizePage) {
        fetchSignedCertificate();
        fetchAuthoritySignatures();
        fetchCertificateSignatures();
      }
    }
  }, [id, setPageTitle]);

  // Close payment dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        paymentDropdownOpen &&
        !target.closest(".user-detail-action-btn--warning")
      ) {
        setPaymentDropdownOpen(false);
      }
    };

    if (paymentDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [paymentDropdownOpen]);

  // Fetch authority signatures
  const fetchAuthoritySignatures = async () => {
    if (!id) return;
    try {
      setAuthoritySignaturesLoading(true);
      const data = await certificateService.getAuthoritySignatures(
        parseInt(id, 10),
      );
      setAuthoritySignatures(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching authority signatures:", err);
      setAuthoritySignatures([]);
    } finally {
      setAuthoritySignaturesLoading(false);
    }
  };

  // Fetch certificate signatures (for signature extraction)
  const fetchCertificateSignatures = async () => {
    if (!id) return;
    try {
      setCertificateSignaturesLoading(true);
      const response = await apiRequest(
        `/CertificateSignatures/by-certificate-application/${id}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        setCertificateSignatures([]);
        return;
      }

      const data = await response.json();
      setCertificateSignatures(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching certificate signatures:", err);
      setCertificateSignatures([]);
    } finally {
      setCertificateSignaturesLoading(false);
    }
  };

  // Check if all required signatures are present
  const areAllSignaturesPresent = (): boolean => {
    if (!application) return false;

    const requiredSignatureCount = application.processAtHeadquarters ? 4 : 7;
    return certificateSignatures.length >= requiredSignatureCount;
  };

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const data = await certificateService.getCertificateApplicationById(
        parseInt(id, 10),
      );
      setApplication(data);

      // Check if workflow is at user's approval level and get current stage
      if (data.workflowId) {
        try {
          const [atMyLevel, currentStage] = await Promise.all([
            workflowService.isAtMyApprovalLevel(data.workflowId, data.id!),
            workflowService.getCurrentStageRole(data.workflowId, data.id!),
          ]);
          setIsAtMyApprovalLevel(atMyLevel);
          setCurrentStageOrder(currentStage.stageOrder);
        } catch (err) {
          console.error("Error checking approval level:", err);
          setIsAtMyApprovalLevel(false);
          setCurrentStageOrder(0);
        }
      } else {
        setIsAtMyApprovalLevel(false);
        setCurrentStageOrder(0);
      }

      // Check for existing payments
      await checkExistingPayments(parseInt(id, 10));

      // Check if ready for submission
      await checkReadyForSubmission(parseInt(id, 10));
    } catch (err: any) {
      console.error("Error fetching application:", err);
      setError(err?.message || "Failed to load application");
    } finally {
      setLoading(false);
    }
  };

  const checkExistingPayments = async (applicationId: number) => {
    try {
      const payments =
        await paymentService.getPaymentsByApplicationId(applicationId);
      setHasExistingPayment(payments && payments.length > 0);
    } catch (err: any) {
      console.error("Error checking payments:", err);
      // Don't show error to user, just assume no payments
      setHasExistingPayment(false);
    }
  };

  const checkReadyForSubmission = async (applicationId: number) => {
    try {
      const readinessChecks = await certificateService.checkReadyForSubmission(
        applicationId,
        ModuleId.Weddings,
      );
      // Check if all values are true
      const allReady = Object.values(readinessChecks).every(
        (value) => value === true,
      );
      setIsReadyForSubmission(allReady);
    } catch (err: any) {
      console.error("Error checking submission readiness:", err);
      // Don't show error to user, just assume not ready
      setIsReadyForSubmission(false);
    }
  };

  const fetchCounties = async () => {
    try {
      const data = await certificateService.getCounties();
      setCounties(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching counties:", err);
    }
  };

  const fetchNationalities = async () => {
    try {
      const data = await certificateService.getNationalities();
      setNationalities(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching nationalities:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const settings = await settingsService.getSettings();
      const ministrySetting = settings.find(
        (s) => s.key === "ministryName" && s.category === "general",
      );
      if (ministrySetting?.value) {
        setMinistryName(ministrySetting.value);
      }
    } catch (err: any) {
      console.error("Error fetching settings:", err);
      // Keep default value on error
    }
  };

  const fetchAttachments = async () => {
    if (!id) return;

    try {
      setAttachmentsLoading(true);
      const response = await apiRequest(`/Attachments/by-application/${id}`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch attachments");
      }

      const data = await response.json();
      const attachmentsArray = Array.isArray(data) ? data : [];
      setAttachments(attachmentsArray);
    } catch (err: any) {
      console.error("Error fetching attachments:", err);
      // Don't show error toast, just log it
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchSignedCertificate = async () => {
    if (!id) return;

    try {
      setSignedCertLoading(true);
      const response = await apiRequest(
        `/SignedCertificates/by-application/${id}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        // Don't throw error, just set null if not found
        setSignedCertificate(null);
        return;
      }

      const data = await response.json();
      // API returns an array, get the first item
      const certArray = Array.isArray(data) ? data : [];
      setSignedCertificate(certArray.length > 0 ? certArray[0] : null);
    } catch (err: any) {
      console.error("Error fetching signed certificate:", err);
      setSignedCertificate(null);
    } finally {
      setSignedCertLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    const confirmed = await showDeleteConfirm(
      attachment.fileName,
      "attachment",
    );

    if (!confirmed) return;

    try {
      setAttachmentsLoading(true);
      const response = await apiRequest(`/Attachments/${attachment.id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete attachment");
      }

      showSuccessToast("Attachment deleted successfully");
      await fetchAttachments();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleDeleteSignedCertificate = async () => {
    if (!signedCertificate) return;

    const confirmed = await showDeleteConfirm(
      "Signed Certificate",
      "signed certificate",
    );

    if (!confirmed) return;

    try {
      setSignedCertLoading(true);
      const response = await apiRequest(
        `/SignedCertificates/${signedCertificate.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete signed certificate");
      }

      showSuccessToast("Signed certificate deleted successfully");
      setSignedCertificate(null);
      await fetchData();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setSignedCertLoading(false);
    }
  };

  // Set default nationality to Liberia when nationalities are loaded and no existing groom
  useEffect(() => {
    if (
      nationalities.length > 0 &&
      !groomApplicant &&
      groomFormData.nationalityId === 0
    ) {
      const liberia = nationalities.find(
        (nat) => nat.name.toLowerCase() === DefaultNationality.toLowerCase(),
      );
      if (liberia) {
        setGroomFormData((prev) => ({
          ...prev,
          nationalityId: liberia.id,
        }));
      }
    }
  }, [nationalities, groomApplicant, groomFormData.nationalityId]);

  // Set default nationality to Liberia for bride as well
  useEffect(() => {
    if (
      nationalities.length > 0 &&
      !brideApplicant &&
      brideFormData.nationalityId === 0
    ) {
      const liberia = nationalities.find(
        (nat) => nat.name.toLowerCase() === DefaultNationality.toLowerCase(),
      );
      if (liberia) {
        setBrideFormData((prev) => ({
          ...prev,
          nationalityId: liberia.id,
        }));
      }
    }
  }, [nationalities, brideApplicant, brideFormData.nationalityId]);

  const fetchOfficiator = async () => {
    if (!id) return;

    try {
      setOfficiatorLoading(true);
      const officiatorData =
        await officiatorService.getOfficiatorByCertificateApplicationId(
          Number(id),
        );

      if (officiatorData) {
        setOfficiator(officiatorData);
        setOfficiatorFormData({
          certificateApplicationId:
            officiatorData.certificateApplicationId || Number(id),
          officiatorName: officiatorData.officiatorName || "",
          officiatorContact: officiatorData.officiatorContact || "",
          witnessName: officiatorData.witnessName || "",
          witnessContact: officiatorData.witnessContact || "",
        });
        // Open accordion in edit mode when officiator record exists
        setOfficiatorAccordionOpen(true);
      } else {
        setOfficiator(null);
        setOfficiatorFormData({
          certificateApplicationId: Number(id),
          officiatorName: "",
          officiatorContact: "",
          witnessName: "",
          witnessContact: "",
        });
      }
      setOfficiatorError("");
    } catch (err: any) {
      setOfficiatorError(err?.message || "Failed to load officiator details");
    } finally {
      setOfficiatorLoading(false);
    }
  };

  const fetchApplicants = async () => {
    if (!id) return;

    try {
      setGroomLoading(true);
      const applicants = await applicantService.getApplicantsByCertificateId(
        Number(id),
      );

      // Find groom (applicantType === 0)
      const groom = applicants.find(
        (app: Applicant) => app.applicantType === ApplicantType.Groom,
      );

      if (groom) {
        setGroomApplicant(groom);

        // Automatically set wasPreviouslyMarried based on existence of previous marriage data
        const hasPreviousMarriageData = !!(
          groom.previousMarriageDate || groom.previousSpouseName
        );

        setGroomFormData({
          userId: groom.userId,
          firstName: groom.firstName || "",
          middleName: groom.middleName || "",
          lastName: groom.lastName || "",
          genderId: groom.genderId || 1,
          dateOfBirth: groom.dateOfBirth ? groom.dateOfBirth.split("T")[0] : "",
          nationalIdNumber: groom.nationalIdNumber || "",
          email: groom.email || "",
          primaryPhone: groom.primaryPhone || "",
          secondaryPhone: groom.secondaryPhone || "",
          certificateApplicationId:
            groom.certificateApplicationId || Number(id),
          applicantType: "Groom",
          countyOfOriginId: groom.countyOfOriginId || 0,
          nationalityId: groom.nationalityId || 0,
          birthCountyId: groom.birthCountyId || 0,
          birthTown: groom.birthTown || "",
          currentAddress: groom.currentAddress || "",
          wasPreviouslyMarried: hasPreviousMarriageData,
          previousMarriageDate: groom.previousMarriageDate
            ? groom.previousMarriageDate.split("T")[0]
            : "",
          previousSpouseName: groom.previousSpouseName || "",
          fatherName: groom.fatherName || "",
          motherName: groom.motherName || "",
          profileImagePath: groom.profileImagePath || "",
        });
        // Set photo preview if exists
        const groomPhotoPath = groom.photoUrl || groom.profileImagePath;
        const groomPhotoUrl = getFullPath(groomPhotoPath);
        if (groomPhotoUrl) {
          setGroomPhotoPreview(groomPhotoUrl);
        }
        // Open accordion in edit mode when groom record exists
        setGroomAccordionOpen(true);
      }

      // Find bride (applicantType === 1)
      const bride = applicants.find(
        (app: Applicant) => app.applicantType === ApplicantType.Bride,
      );

      if (bride) {
        setBrideApplicant(bride);

        // Automatically set wasPreviouslyMarried based on existence of previous marriage data
        const hasPreviousMarriageData = !!(
          bride.previousMarriageDate || bride.previousSpouseName
        );

        setBrideFormData({
          userId: bride.userId,
          firstName: bride.firstName || "",
          middleName: bride.middleName || "",
          lastName: bride.lastName || "",
          genderId: bride.genderId || 2,
          dateOfBirth: bride.dateOfBirth ? bride.dateOfBirth.split("T")[0] : "",
          nationalIdNumber: bride.nationalIdNumber || "",
          email: bride.email || "",
          primaryPhone: bride.primaryPhone || "",
          secondaryPhone: bride.secondaryPhone || "",
          certificateApplicationId:
            bride.certificateApplicationId || Number(id),
          applicantType: "Bride",
          countyOfOriginId: bride.countyOfOriginId || 0,
          nationalityId: bride.nationalityId || 0,
          birthCountyId: bride.birthCountyId || 0,
          birthTown: bride.birthTown || "",
          currentAddress: bride.currentAddress || "",
          wasPreviouslyMarried: hasPreviousMarriageData,
          previousMarriageDate: bride.previousMarriageDate
            ? bride.previousMarriageDate.split("T")[0]
            : "",
          previousSpouseName: bride.previousSpouseName || "",
          fatherName: bride.fatherName || "",
          motherName: bride.motherName || "",
          profileImagePath: bride.profileImagePath || "",
          brideProposedName: bride.brideProposedName || "",
        });
        // Set photo preview if exists
        const bridePhotoPath = bride.photoUrl || bride.profileImagePath;
        const bridePhotoUrl = getFullPath(bridePhotoPath);
        if (bridePhotoUrl) {
          setBridePhotoPreview(bridePhotoUrl);
        }
        // Open accordion in edit mode when bride record exists
        setBrideAccordionOpen(true);
      }
    } catch (err: any) {
      setGroomError(err?.message || "Failed to load groom details");
      setBrideError(err?.message || "Failed to load bride details");
    } finally {
      setGroomLoading(false);
      setBrideLoading(false);
    }
  };

  const handleBrideFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setBrideFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "number") {
      setBrideFormData((prev) => ({ ...prev, [name]: Number(nextValue) }));
    } else {
      setBrideFormData((prev) => ({ ...prev, [name]: nextValue }));
    }

    // Clear validation error for this field when user starts typing
    if (brideValidationErrors[name]) {
      setBrideValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleGroomFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setGroomFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "number") {
      setGroomFormData((prev) => ({ ...prev, [name]: Number(nextValue) }));
    } else {
      setGroomFormData((prev) => ({ ...prev, [name]: nextValue }));
    }

    // Clear validation error for this field when user starts typing
    if (groomValidationErrors[name]) {
      setGroomValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleGroomPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setGroomError("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setGroomError("Image size must be less than 5MB");
        return;
      }
      setGroomPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setGroomPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeGroomPhoto = () => {
    setGroomPhotoFile(null);
    setGroomPhotoPreview(null);
    setGroomFormData({ ...groomFormData, profileImagePath: "" });
  };

  const handleBridePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setBrideError("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setBrideError("Image size must be less than 5MB");
        return;
      }
      setBridePhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBridePhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBridePhoto = () => {
    setBridePhotoFile(null);
    setBridePhotoPreview(null);
    setBrideFormData({ ...brideFormData, profileImagePath: "" });
  };

  const validateBrideForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!brideFormData.firstName?.trim()) {
      errors.firstName = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.lastName?.trim()) {
      errors.lastName = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.dateOfBirth) {
      errors.dateOfBirth = REQUIRED_FIELD_MESSAGE;
    }
    const bridePrimaryPhoneError = validatePhoneNumber(
      brideFormData.primaryPhone,
      {
        required: true,
      },
    );
    if (bridePrimaryPhoneError) {
      errors.primaryPhone = bridePrimaryPhoneError;
    }
    const brideSecondaryPhoneError = validatePhoneNumber(
      brideFormData.secondaryPhone,
    );
    if (brideSecondaryPhoneError) {
      errors.secondaryPhone = brideSecondaryPhoneError;
    }
    if (
      !brideFormData.countyOfOriginId ||
      brideFormData.countyOfOriginId === 0
    ) {
      errors.countyOfOriginId = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.birthCountyId || brideFormData.birthCountyId === 0) {
      errors.birthCountyId = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.birthTown?.trim()) {
      errors.birthTown = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.nationalityId || brideFormData.nationalityId === 0) {
      errors.nationalityId = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.currentAddress?.trim()) {
      errors.currentAddress = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.fatherName?.trim()) {
      errors.fatherName = REQUIRED_FIELD_MESSAGE;
    }
    if (!brideFormData.motherName?.trim()) {
      errors.motherName = REQUIRED_FIELD_MESSAGE;
    }
    // Photo is required for new applicants
    if (!brideApplicant && !bridePhotoFile) {
      errors.photo = REQUIRED_FIELD_MESSAGE;
    }

    setBrideValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateGroomForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!groomFormData.firstName?.trim()) {
      errors.firstName = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.lastName?.trim()) {
      errors.lastName = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.dateOfBirth) {
      errors.dateOfBirth = REQUIRED_FIELD_MESSAGE;
    }
    const groomPrimaryPhoneError = validatePhoneNumber(
      groomFormData.primaryPhone,
      {
        required: true,
      },
    );
    if (groomPrimaryPhoneError) {
      errors.primaryPhone = groomPrimaryPhoneError;
    }
    const groomSecondaryPhoneError = validatePhoneNumber(
      groomFormData.secondaryPhone,
    );
    if (groomSecondaryPhoneError) {
      errors.secondaryPhone = groomSecondaryPhoneError;
    }
    if (
      !groomFormData.countyOfOriginId ||
      groomFormData.countyOfOriginId === 0
    ) {
      errors.countyOfOriginId = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.birthCountyId || groomFormData.birthCountyId === 0) {
      errors.birthCountyId = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.birthTown?.trim()) {
      errors.birthTown = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.nationalityId || groomFormData.nationalityId === 0) {
      errors.nationalityId = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.currentAddress?.trim()) {
      errors.currentAddress = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.fatherName?.trim()) {
      errors.fatherName = REQUIRED_FIELD_MESSAGE;
    }
    if (!groomFormData.motherName?.trim()) {
      errors.motherName = REQUIRED_FIELD_MESSAGE;
    }
    // Photo is required for new applicants
    if (!groomApplicant && !groomPhotoFile) {
      errors.photo = REQUIRED_FIELD_MESSAGE;
    }

    setGroomValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBrideSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBrideError("");

    if (!application) return;

    // Validate form
    if (!validateBrideForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Bride Information",
      "Are you sure you want to submit the bride's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setBrideLoading(true);

      // Destructure wasPreviouslyMarried to exclude it from the payload
      const { wasPreviouslyMarried, profileImagePath, ...brideData } =
        brideFormData;

      // Clean up previous marriage fields - send undefined if empty or not previously married
      const cleanedData = {
        ...brideData,
        previousMarriageDate:
          brideFormData.wasPreviouslyMarried &&
          brideFormData.previousMarriageDate
            ? brideFormData.previousMarriageDate
            : undefined,
        previousSpouseName:
          brideFormData.wasPreviouslyMarried && brideFormData.previousSpouseName
            ? brideFormData.previousSpouseName
            : undefined,
      };

      if (brideApplicant) {
        // UPDATE existing bride
        const payload = {
          ...cleanedData,
          id: brideApplicant.id,
          userId: brideApplicant.userId,
          certificateApplicationId: application.id,
          applicantType: "Bride",
          roleId: BasicRole,
        };
        await applicantService.updateApplicant(
          brideApplicant.id!,
          payload as UpdateApplicantDto,
          bridePhotoFile || undefined,
        );
        showSuccessToast("Bride details updated successfully");
      } else {
        // CREATE new bride
        const payload = {
          ...cleanedData,
          certificateApplicationId: application.id,
          applicantType: "Bride",
          roleId: BasicRole,
        };
        await applicantService.createApplicant(
          payload as CreateApplicantDto,
          bridePhotoFile || undefined,
        );
        showSuccessToast("Bride details saved successfully");
      }

      // Refresh applicant data
      await fetchApplicants();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      setBrideError(err?.message || "Failed to save bride details");
    } finally {
      setBrideLoading(false);
    }
  };

  const handleGroomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGroomError("");

    if (!application) return;

    // Validate form
    if (!validateGroomForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Groom Information",
      "Are you sure you want to submit the groom's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setGroomLoading(true);

      // Destructure wasPreviouslyMarried to exclude it from the payload
      const { wasPreviouslyMarried, profileImagePath, ...groomData } =
        groomFormData;

      // Clean up previous marriage fields - send undefined if empty or not previously married
      const cleanedData = {
        ...groomData,
        previousMarriageDate:
          groomFormData.wasPreviouslyMarried &&
          groomFormData.previousMarriageDate
            ? groomFormData.previousMarriageDate
            : undefined,
        previousSpouseName:
          groomFormData.wasPreviouslyMarried && groomFormData.previousSpouseName
            ? groomFormData.previousSpouseName
            : undefined,
      };

      if (groomApplicant) {
        // UPDATE existing groom
        const payload = {
          ...cleanedData,
          id: groomApplicant.id,
          userId: groomApplicant.userId,
          certificateApplicationId: application.id,
          applicantType: "Groom",
          roleId: BasicRole,
        };
        await applicantService.updateApplicant(
          groomApplicant.id!,
          payload as UpdateApplicantDto,
          groomPhotoFile || undefined,
        );
        showSuccessToast("Groom details updated successfully");
      } else {
        // CREATE new groom
        const payload = {
          ...cleanedData,
          certificateApplicationId: application.id,
          applicantType: "Groom",
          roleId: BasicRole,
        };
        await applicantService.createApplicant(
          payload as CreateApplicantDto,
          groomPhotoFile || undefined,
        );
        showSuccessToast("Groom details saved successfully");
      }

      // Refresh applicant data
      await fetchApplicants();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      setGroomError(err?.message || "Failed to save groom details");
    } finally {
      setGroomLoading(false);
    }
  };

  const handleDeleteGroom = async () => {
    if (!groomApplicant) return;

    const confirmed = await showDeleteConfirm(
      groomApplicant.firstName + " " + groomApplicant.lastName,
      "groom applicant",
    );

    if (!confirmed) return;

    try {
      setGroomLoading(true);
      await applicantService.deleteApplicant(groomApplicant.id!);
      showSuccessToast("Groom details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setGroomApplicant(null);
      // Reset photo state
      setGroomPhotoFile(null);
      setGroomPhotoPreview(null);
      // Reset form to initial state
      setGroomFormData({
        userId: undefined,
        firstName: "",
        middleName: "",
        lastName: "",
        genderId: 1,
        dateOfBirth: "",
        nationalIdNumber: "",
        email: "",
        primaryPhone: "",
        secondaryPhone: "",
        certificateApplicationId: application?.id || 0,
        applicantType: "Groom",
        countyOfOriginId: 0,
        nationalityId: 0,
        birthCountyId: 0,
        birthTown: "",
        currentAddress: "",
        wasPreviouslyMarried: false,
        previousMarriageDate: "",
        previousSpouseName: "",
        fatherName: "",
        motherName: "",
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setGroomLoading(false);
    }
  };

  const handleDeleteBride = async () => {
    if (!brideApplicant) return;

    const confirmed = await showDeleteConfirm(
      brideApplicant.firstName + " " + brideApplicant.lastName,
      "bride applicant",
    );

    if (!confirmed) return;

    try {
      setBrideLoading(true);
      await applicantService.deleteApplicant(brideApplicant.id!);
      showSuccessToast("Bride details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setBrideApplicant(null);
      // Reset photo state
      setBridePhotoFile(null);
      setBridePhotoPreview(null);
      // Reset form to initial state
      setBrideFormData({
        userId: undefined,
        firstName: "",
        middleName: "",
        lastName: "",
        genderId: 2,
        dateOfBirth: "",
        nationalIdNumber: "",
        email: "",
        primaryPhone: "",
        secondaryPhone: "",
        certificateApplicationId: application?.id || 0,
        applicantType: "Bride",
        countyOfOriginId: 0,
        nationalityId: 0,
        birthCountyId: 0,
        birthTown: "",
        currentAddress: "",
        wasPreviouslyMarried: false,
        previousMarriageDate: "",
        previousSpouseName: "",
        fatherName: "",
        motherName: "",
        brideProposedName: "",
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setBrideLoading(false);
    }
  };

  const handleOfficiatorFormChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "officiatorContact" || name === "witnessContact"
        ? sanitizePhoneNumberInput(value)
        : value;
    setOfficiatorFormData((prev) => ({ ...prev, [name]: nextValue }));

    // Clear validation error for this field when user starts typing
    if (officiatorValidationErrors[name]) {
      setOfficiatorValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateOfficiatorForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!officiatorFormData.officiatorName?.trim()) {
      errors.officiatorName = REQUIRED_FIELD_MESSAGE;
    }
    const officiatorContactError = validatePhoneNumber(
      officiatorFormData.officiatorContact,
      { required: true },
    );
    if (officiatorContactError) {
      errors.officiatorContact = officiatorContactError;
    }
    if (!officiatorFormData.witnessName?.trim()) {
      errors.witnessName = REQUIRED_FIELD_MESSAGE;
    }
    const witnessContactError = validatePhoneNumber(
      officiatorFormData.witnessContact,
      { required: true },
    );
    if (witnessContactError) {
      errors.witnessContact = witnessContactError;
    }

    setOfficiatorValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOfficiatorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOfficiatorError("");

    if (!application) return;

    // Validate form
    if (!validateOfficiatorForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Officiator Information",
      "Are you sure you want to submit the officiator's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setOfficiatorLoading(true);

      if (officiator) {
        // UPDATE existing officiator
        const payload = {
          ...officiatorFormData,
          id: officiator.id,
          certificateApplicationId: application.id,
        };
        await officiatorService.updateOfficiator(
          officiator.id!,
          payload as UpdateOfficiatorDto,
        );
        showSuccessToast("Officiator details updated successfully");
      } else {
        // CREATE new officiator
        const payload = {
          ...officiatorFormData,
          certificateApplicationId: application.id!,
        };
        await officiatorService.createOfficiator(payload);
        showSuccessToast("Officiator details saved successfully");
      }

      // Refresh officiator data
      await fetchOfficiator();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      setOfficiatorError(err?.message || "Failed to save officiator details");
    } finally {
      setOfficiatorLoading(false);
    }
  };

  const handleDeleteOfficiator = async () => {
    if (!officiator) return;

    const confirmed = await showDeleteConfirm(
      officiator.officiatorName,
      "officiator details",
    );

    if (!confirmed) return;

    try {
      setOfficiatorLoading(true);
      await officiatorService.deleteOfficiator(officiator.id!);
      showSuccessToast("Officiator details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setOfficiator(null);
      // Reset form to initial state
      setOfficiatorFormData({
        certificateApplicationId: application?.id || 0,
        officiatorName: "",
        officiatorContact: "",
        witnessName: "",
        witnessContact: "",
      });
    } catch (err: any) {
      // Show confirmation dialog
      const confirmed = await showConfirm(
        "Submit Application",
        "Are you sure you want to submit this application? This action cannot be undone.",
        "Yes, submit",
        "Cancel",
      );
      if (!confirmed) return;

      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setOfficiatorLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!application) return;

    try {
      setActionLoading(true);
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Submitted,
      );
      showSuccessToast("Application submitted successfully");
      // Refresh the application data to show updated status
      await fetchData();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!application || !application.workflowId) return;
    const confirmed = await showConfirm(
      "Approve Application",
      `Are you sure you want to approve this marriage certificate application?`,
      "Yes, approve",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);

      // Get workflow details to check if this is the final stage
      const workflow = await workflowService.getWorkflowById(
        application.workflowId,
      );
      const totalStages = workflow.stages?.length || 0;
      const isFinalStage = currentStageOrder >= totalStages;

      // Approve the workflow action
      await workflowService.approve({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: "Application approved",
      });

      // If this is the final approval stage, update status to Approved
      if (isFinalStage) {
        await certificateService.changeStatus(
          application.id!,
          UserStatusId.Approved,
        );
      }

      await fetchData();
      showSuccessToast("Application has been approved successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuery = () => {
    if (!application || !application.workflowId) return;
    setShowQueryForm(true);
  };

  const handleSubmitQuery = async (payload: QueryWorkflowActionDto) => {
    if (!application || !application.workflowId) return;
    try {
      setActionLoading(true);

      await workflowService.query(payload);

      // Change status to Queried to move the workflow back one step
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Queried,
      );

      await fetchData();
      showSuccessToast("Application has been queried successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!application || !application.workflowId) return;

    // Import Swal for custom input dialog
    const Swal = (await import("sweetalert2")).default;

    const result = await Swal.fire({
      title: "Reject Application",
      html: `
        <p>Please provide a reason for rejecting this application:</p>
      `,
      input: "textarea",
      inputPlaceholder: "Enter your rejection reason here...",
      inputAttributes: {
        "aria-label": "Rejection reason",
        style: "min-height: 100px;",
      },
      showCancelButton: true,
      confirmButtonText: "Reject Application",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "You must provide a reason for rejection!";
        }
      },
    });

    if (!result.isConfirmed || !result.value || !result.value.trim()) return;

    const rejectComment = result.value.trim();

    try {
      setActionLoading(true);

      // Reject the workflow action with the provided comment
      await workflowService.reject({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: rejectComment,
      });

      // Change status to Rejected
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Rejected,
      );

      await fetchData();
      showSuccessToast("Application has been rejected successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const resetEditForm = () => {
    setEditFormData({});
    setEditError("");
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError("");
    if (!application) return;

    try {
      setEditLoading(true);
      await certificateService.updateCertificateApplication(
        application.id!,
        editFormData,
      );
      setShowEditModal(false);
      resetEditForm();
      await fetchData();
      showSuccessToast("Application updated successfully.");
    } catch (err: any) {
      setEditError(err?.message || "Failed to update application");
    } finally {
      setEditLoading(false);
    }
  };

  const handlePayment = (method: string) => {
    setPaymentDropdownOpen(false);

    // Show payment modal or navigate
    switch (method) {
      case "Manual":
        openManualPaymentModal();
        break;
      case "Card":
        openCardPaymentModal();
        break;
      case "Mobile Money":
        window.open("https://paymentcollectclient.triaxo.com/", "_blank");
        break;
      default:
        showSuccessToast(`Redirecting to ${method} payment...`);
    }
  };

  const openCardPaymentModal = () => {
    if (!application?.id) return;
    // CardPayment modal uses Stripe and doesn't need lookup data
    setShowCardPaymentModal(true);
  };

  const openManualPaymentModal = () => {
    if (!application?.id) return;
    setShowManualPaymentModal(true);
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Determine required permission based on the page type
  const getRequiredPermission = () => {
    if (isPrintPage) {
      return ApplicationPermissionId.Print;
    }
    if (isFinalizePage) {
      return ApplicationPermissionId.View;
    }
    return ApplicationPermissionId.View;
  };

  const requiredPermission = getRequiredPermission();

  if (!HasPermission(ModuleId.Weddings, requiredPermission)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>
              You do not have permission to view marriage certificate
              applications
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      {!isPrintPage && (
        <>
          <Topbar
            onMenuToggle={() => setSidebarOpen((prev) => !prev)}
            sidebarOpen={sidebarOpen}
          />
          <Sidebar isOpen={sidebarOpen} />
        </>
      )}
      <main className="main-content">
        <div className="user-detail-page">
          <BusyOverlay
            isVisible={loading || actionLoading}
            message={
              actionLoading ? "Processing..." : "Loading application details..."
            }
          />

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
              <button onClick={() => setError("")} className="alert-close">
                ✕
              </button>
            </div>
          )}

          {/* Print View - Marriage Certificate */}
          {isPrintPage && application && groomApplicant && brideApplicant && (
            <>
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  background: "#fff",
                }}
                className="no-print"
              >
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: "12px 32px",
                    fontSize: "16px",
                    background: "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "600",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  🖨️ Print Certificate
                </button>
                <button
                  onClick={() => navigate(getBackPath())}
                  style={{
                    padding: "12px 32px",
                    fontSize: "16px",
                    background: "#6b7280",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "600",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginLeft: "10px",
                  }}
                >
                  ← Back
                </button>
              </div>
              <CountyMarriageCert
                groomName={`${groomApplicant.firstName} ${groomApplicant.middleName || ""} ${groomApplicant.lastName}`.trim()}
                groomDateOfBirth={groomApplicant.dateOfBirth || ""}
                groomPlaceOfBirth={groomApplicant.birthTown || ""}
                groomCountyOfBirth={
                  counties.find((c) => c.id === groomApplicant.birthCountyId)
                    ?.name || ""
                }
                groomFatherName={groomApplicant.fatherName || ""}
                groomMotherName={groomApplicant.motherName || ""}
                brideName={`${brideApplicant.firstName} ${brideApplicant.middleName || ""} ${brideApplicant.lastName}`.trim()}
                brideProposedName={brideApplicant.brideProposedName || ""}
                brideDateOfBirth={brideApplicant.dateOfBirth || ""}
                bridePlaceOfBirth={brideApplicant.birthTown || ""}
                brideCountyOfBirth={
                  counties.find((c) => c.id === brideApplicant.birthCountyId)
                    ?.name || ""
                }
                brideFatherName={brideApplicant.fatherName || ""}
                brideMotherName={brideApplicant.motherName || ""}
                placeOfWedding={application.placeOfWedding || ""}
                dateOfWedding={application.dateOfWedding || ""}
                countyName={application.countyName || ""}
                certificateCode={application.formNo || ""}
                revenueNumber=""
                ministryName={ministryName}
                groomPhotoUrl={
                  groomApplicant.photoUrl || groomApplicant.profileImagePath
                }
                bridePhotoUrl={
                  brideApplicant.photoUrl || brideApplicant.profileImagePath
                }
                certificateApplicationId={application.id}
              />
            </>
          )}

          {application && !isPrintPage && (
            <div className="user-detail-layout">
              <button
                className="user-detail-back user-detail-back--mobile"
                onClick={() => navigate(getBackPath())}
              >
                ← Back to Applications
              </button>

              {/* Column 1: Application Details */}
              <div className="user-detail-info">
                {/* Profile header */}
                <div className="user-detail-profile-header">
                  <div className="user-detail-avatar">💍</div>
                  <div className="user-detail-profile-info">
                    <h2>{application.formNo}</h2>
                    <div className="user-detail-profile-meta">
                      <span>{application.category}</span>
                      <span className="divider">•</span>
                      <span className={`status-badge status-badge--active`}>
                        {application.status?.name ||
                          application.statusName ||
                          "Pending"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Application Information */}
                <h3 className="user-detail-section-title">
                  Application Information
                </h3>
                <div
                  className="user-detail-grid"
                  style={{
                    gridTemplateColumns: "repeat(3, 1fr)",
                  }}
                >
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Category</div>
                    <div className="user-detail-field-value">
                      {application.category}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">County</div>
                    <div
                      className={`user-detail-field-value ${!application.countyName ? "empty" : ""}`}
                    >
                      {application.countyName || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Place of Marriage
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.placeOfWedding ? "empty" : ""}`}
                    >
                      {application.placeOfWedding || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Marriage
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfWedding ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfWedding) || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Certification
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfCertification ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfCertification) || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Email</div>
                    <div
                      className={`user-detail-field-value ${!application.email ? "empty" : ""}`}
                    >
                      {application.email || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Created Date</div>
                    <div
                      className={`user-detail-field-value ${!application.createdAt ? "empty" : ""}`}
                    >
                      {formatDate(application.createdAt) || "—"}
                    </div>
                  </div>
                </div>

                {/* Signed Certificate Display (Finalize Page Only) */}
                {isFinalizePage && signedCertificate && (
                  <div style={{ marginTop: "2rem" }}>
                    <div
                      style={{
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "1rem",
                          marginBottom: "0.5rem",
                          color: "#28a745",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>📄 Signed Certificate</span>
                        <button
                          onClick={handleDeleteSignedCertificate}
                          disabled={signedCertLoading}
                          style={{
                            background: "#dc3545",
                            color: "#fff",
                            border: "none",
                            padding: "0.5rem 1rem",
                            borderRadius: "4px",
                            cursor: signedCertLoading
                              ? "not-allowed"
                              : "pointer",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            opacity: signedCertLoading ? 0.6 : 1,
                          }}
                          title="Delete signed certificate"
                        >
                          {signedCertLoading ? (
                            <>
                              <span className="spinner spinner--sm"></span>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <span>🗑️</span>
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          marginBottom: "1rem",
                        }}
                      >
                        Uploaded on:{" "}
                        {new Date(signedCertificate.createdAt).toLocaleString()}
                      </div>
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          overflow: "hidden",
                          background: "#fff",
                        }}
                      >
                        {signedCertificate.documentPath ? (
                          (() => {
                            const fullUrl = getFullPath(
                              signedCertificate.documentPath,
                            );
                            if (!fullUrl) {
                              return (
                                <div
                                  style={{
                                    padding: "2rem",
                                    textAlign: "center",
                                    color: "#6b7280",
                                  }}
                                >
                                  Unable to display certificate
                                </div>
                              );
                            }
                            return signedCertificate.documentPath
                              .toLowerCase()
                              .endsWith(".pdf") ? (
                              <iframe
                                src={fullUrl || undefined}
                                title="Signed Certificate"
                                style={{
                                  width: "100%",
                                  height: "600px",
                                  border: "none",
                                }}
                              />
                            ) : (
                              <img
                                src={fullUrl || undefined}
                                alt="Signed Certificate"
                                style={{
                                  width: "100%",
                                  maxHeight: "800px",
                                  objectFit: "contain",
                                }}
                              />
                            );
                          })()
                        ) : (
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            Unable to display certificate
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Groom's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "2rem" }}>
                    <div
                      onClick={() => setGroomAccordionOpen(!groomAccordionOpen)}
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Groom's Detail</span>
                      <span>{groomAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {groomAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleGroomSubmit}>
                          {groomError && (
                            <div
                              style={{
                                padding: "0.75rem",
                                marginBottom: "1rem",
                                background: "#fee",
                                color: "#c00",
                                borderRadius: "4px",
                                border: "1px solid #fcc",
                              }}
                            >
                              {groomError}
                            </div>
                          )}

                          {/* Photo Upload */}
                          <div
                            style={{
                              marginBottom: "1.5rem",
                            }}
                          >
                            <label
                              style={{
                                display: "block",
                                marginBottom: "0.5rem",
                                fontWeight: "500",
                                fontSize: "0.875rem",
                                color: "#374151",
                              }}
                            >
                              Passport Size Photo{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <div className="photo-upload-container">
                              {groomPhotoPreview ? (
                                <div className="photo-preview">
                                  <img
                                    src={
                                      getFullPath(groomPhotoPreview) ||
                                      undefined
                                    }
                                    alt="Groom Preview"
                                    className="preview-image"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement;
                                      img.style.border = "2px solid red";
                                      img.alt = "Failed to load image";
                                    }}
                                  />
                                  {!isFinalizePage && (
                                    <button
                                      type="button"
                                      className="btn btn--sm btn--danger"
                                      onClick={removeGroomPhoto}
                                    >
                                      Remove Photo
                                    </button>
                                  )}
                                </div>
                              ) : (
                                !isFinalizePage && (
                                  <div className="photo-upload-area">
                                    <input
                                      type="file"
                                      id="groomPhotoUpload"
                                      accept="image/*"
                                      onChange={handleGroomPhotoChange}
                                      style={{ display: "none" }}
                                    />
                                    <label
                                      htmlFor="groomPhotoUpload"
                                      className="photo-upload-label"
                                    >
                                      <div className="upload-icon">📷</div>
                                      <div className="upload-text">
                                        <span className="upload-title">
                                          Click to upload photo
                                        </span>
                                        <span className="upload-subtitle">
                                          PNG, JPG up to 5MB
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                            {groomValidationErrors.photo && (
                              <div className="form-error">
                                {groomValidationErrors.photo}
                              </div>
                            )}
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${groomFormData.firstName ? "has-value" : ""}`}
                            >
                              <label className="form-label">First Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="firstName"
                                value={groomFormData.firstName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.firstName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.firstName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.middleName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Middle Name</label>
                              <input
                                type="text"
                                className="form-input"
                                name="middleName"
                                value={groomFormData.middleName}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${groomFormData.lastName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Last Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="lastName"
                                value={groomFormData.lastName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.lastName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.lastName}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">Gender *</label>
                              <select
                                className="form-input"
                                name="genderId"
                                value={groomFormData.genderId}
                                onChange={handleGroomFormChange}
                                required
                                disabled
                                style={{
                                  background: "#f1f1f1",
                                  cursor: "not-allowed",
                                }}
                              >
                                <option value={1}>Male</option>
                                <option value={2}>Female</option>
                              </select>
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                className="form-input"
                                name="dateOfBirth"
                                value={groomFormData.dateOfBirth}
                                onChange={handleGroomFormChange}
                                max={getMaxDateFor18Plus()}
                                required
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.dateOfBirth && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.dateOfBirth}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.nationalIdNumber ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                National ID Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="nationalIdNumber"
                                value={groomFormData.nationalIdNumber}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${groomFormData.primaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="primaryPhone"
                                value={groomFormData.primaryPhone}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.primaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.secondaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="secondaryPhone"
                                value={groomFormData.secondaryPhone}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.secondaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.secondaryPhone}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Origin & Birth Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div className="form-group has-value">
                              <label className="form-label">
                                County of Origin *
                              </label>
                              <select
                                className="form-input"
                                name="countyOfOriginId"
                                value={groomFormData.countyOfOriginId}
                                onChange={handleGroomFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {groomValidationErrors.countyOfOriginId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.countyOfOriginId}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Birth County *
                              </label>
                              <select
                                className="form-input"
                                name="birthCountyId"
                                value={groomFormData.birthCountyId}
                                onChange={handleGroomFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {groomValidationErrors.birthCountyId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.birthCountyId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.birthTown ? "has-value" : ""}`}
                            >
                              <label className="form-label">Birth Town *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="birthTown"
                                value={groomFormData.birthTown}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.birthTown && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.birthTown}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Nationality *
                              </label>
                              <select
                                className="form-input"
                                name="nationalityId"
                                value={groomFormData.nationalityId}
                                onChange={handleGroomFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select Nationality</option>
                                {nationalities.map((nationality) => (
                                  <option
                                    key={nationality.id}
                                    value={nationality.id}
                                  >
                                    {nationality.name}
                                  </option>
                                ))}
                              </select>
                              {groomValidationErrors.nationalityId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.nationalityId}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${groomFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={groomFormData.email}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>

                            <div
                              className={`form-group ${groomFormData.currentAddress ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Current Address *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="currentAddress"
                                value={groomFormData.currentAddress}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.currentAddress && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.currentAddress}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Family Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${groomFormData.fatherName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Father's Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="fatherName"
                                value={groomFormData.fatherName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.fatherName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.fatherName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.motherName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Mother's Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="motherName"
                                value={groomFormData.motherName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.motherName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {groomValidationErrors.motherName}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Previous Marriage Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div className="switch-container">
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  name="wasPreviouslyMarried"
                                  checked={groomFormData.wasPreviouslyMarried}
                                  onChange={handleGroomFormChange}
                                  disabled={isFinalizePage}
                                />
                                <span className="switch-slider"></span>
                              </label>
                              <span className="switch-label">
                                Was Previously Married
                              </span>
                            </div>
                            {groomFormData.wasPreviouslyMarried && (
                              <>
                                <div className="form-group has-value">
                                  <label className="form-label">
                                    Previous Marriage Date
                                  </label>
                                  <input
                                    type="date"
                                    className="form-input"
                                    name="previousMarriageDate"
                                    value={groomFormData.previousMarriageDate}
                                    onChange={handleGroomFormChange}
                                    max={getMaxDateFor18Plus()}
                                    readOnly={isFinalizePage}
                                  />
                                </div>
                                <div
                                  className={`form-group ${groomFormData.previousSpouseName ? "has-value" : ""}`}
                                >
                                  <label className="form-label">
                                    Previous Spouse Name
                                  </label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    name="previousSpouseName"
                                    value={groomFormData.previousSpouseName}
                                    onChange={handleGroomFormChange}
                                    placeholder=" "
                                    readOnly={isFinalizePage}
                                  />
                                </div>
                              </>
                            )}
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {groomApplicant && (
                                <button
                                  type="button"
                                  onClick={handleDeleteGroom}
                                  disabled={groomLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: groomLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: groomLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Groom
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={groomLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: groomLoading ? "#ccc" : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: groomLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {groomLoading
                                  ? "Saving..."
                                  : groomApplicant
                                    ? "Update Groom"
                                    : "Save Groom"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Bride's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() => setBrideAccordionOpen(!brideAccordionOpen)}
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Bride's Detail</span>
                      <span>{brideAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {brideAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleBrideSubmit}>
                          {brideError && (
                            <div
                              style={{
                                padding: "0.75rem",
                                marginBottom: "1rem",
                                background: "#fee",
                                color: "#c00",
                                borderRadius: "4px",
                                border: "1px solid #fcc",
                              }}
                            >
                              {brideError}
                            </div>
                          )}

                          {/* Photo Upload */}
                          <div
                            style={{
                              marginBottom: "1.5rem",
                            }}
                          >
                            <label
                              style={{
                                display: "block",
                                marginBottom: "0.5rem",
                                fontWeight: "500",
                                fontSize: "0.875rem",
                                color: "#374151",
                              }}
                            >
                              Passport Size Photo{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <div className="photo-upload-container">
                              {bridePhotoPreview ? (
                                <div className="photo-preview">
                                  <img
                                    src={
                                      getFullPath(bridePhotoPreview) ||
                                      undefined
                                    }
                                    alt="Bride Preview"
                                    className="preview-image"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement;
                                      img.style.border = "2px solid red";
                                      img.alt = "Failed to load image";
                                    }}
                                  />
                                  {!isFinalizePage && (
                                    <button
                                      type="button"
                                      className="btn btn--sm btn--danger"
                                      onClick={removeBridePhoto}
                                    >
                                      Remove Photo
                                    </button>
                                  )}
                                </div>
                              ) : (
                                !isFinalizePage && (
                                  <div className="photo-upload-area">
                                    <input
                                      type="file"
                                      id="bridePhotoUpload"
                                      accept="image/*"
                                      onChange={handleBridePhotoChange}
                                      style={{ display: "none" }}
                                    />
                                    <label
                                      htmlFor="bridePhotoUpload"
                                      className="photo-upload-label"
                                    >
                                      <div className="upload-icon">📷</div>
                                      <div className="upload-text">
                                        <span className="upload-title">
                                          Click to upload photo
                                        </span>
                                        <span className="upload-subtitle">
                                          PNG, JPG up to 5MB
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                            {brideValidationErrors.photo && (
                              <div className="form-error">
                                {brideValidationErrors.photo}
                              </div>
                            )}
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${brideFormData.firstName ? "has-value" : ""}`}
                            >
                              <label className="form-label">First Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="firstName"
                                value={brideFormData.firstName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.firstName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.firstName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.middleName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Middle Name</label>
                              <input
                                type="text"
                                className="form-input"
                                name="middleName"
                                value={brideFormData.middleName}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${brideFormData.lastName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Last Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="lastName"
                                value={brideFormData.lastName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.lastName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.lastName}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">Gender *</label>
                              <select
                                className="form-input"
                                name="genderId"
                                value={brideFormData.genderId}
                                onChange={handleBrideFormChange}
                                required
                                disabled
                                style={{
                                  background: "#f1f1f1",
                                  cursor: "not-allowed",
                                }}
                              >
                                <option value={1}>Male</option>
                                <option value={2}>Female</option>
                              </select>
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                className="form-input"
                                name="dateOfBirth"
                                value={brideFormData.dateOfBirth}
                                onChange={handleBrideFormChange}
                                max={getMaxDateFor18Plus()}
                                required
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.dateOfBirth && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.dateOfBirth}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.nationalIdNumber ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                National ID Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="nationalIdNumber"
                                value={brideFormData.nationalIdNumber}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${brideFormData.primaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="primaryPhone"
                                value={brideFormData.primaryPhone}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.primaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.secondaryPhone ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="tel"
                                className="form-input"
                                name="secondaryPhone"
                                value={brideFormData.secondaryPhone}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.secondaryPhone && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.secondaryPhone}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Origin & Birth Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div className="form-group has-value">
                              <label className="form-label">
                                County of Origin *
                              </label>
                              <select
                                className="form-input"
                                name="countyOfOriginId"
                                value={brideFormData.countyOfOriginId}
                                onChange={handleBrideFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {brideValidationErrors.countyOfOriginId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.countyOfOriginId}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Birth County *
                              </label>
                              <select
                                className="form-input"
                                name="birthCountyId"
                                value={brideFormData.birthCountyId}
                                onChange={handleBrideFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {brideValidationErrors.birthCountyId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.birthCountyId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.birthTown ? "has-value" : ""}`}
                            >
                              <label className="form-label">Birth Town *</label>
                              <input
                                type="text"
                                className="form-input"
                                name="birthTown"
                                value={brideFormData.birthTown}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.birthTown && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.birthTown}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">
                                Nationality *
                              </label>
                              <select
                                className="form-input"
                                name="nationalityId"
                                value={brideFormData.nationalityId}
                                onChange={handleBrideFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select Nationality</option>
                                {nationalities.map((nationality) => (
                                  <option
                                    key={nationality.id}
                                    value={nationality.id}
                                  >
                                    {nationality.name}
                                  </option>
                                ))}
                              </select>
                              {brideValidationErrors.nationalityId && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.nationalityId}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${brideFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={brideFormData.email}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>

                            <div
                              className={`form-group ${brideFormData.currentAddress ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Current Address *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="currentAddress"
                                value={brideFormData.currentAddress}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.currentAddress && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.currentAddress}
                                </div>
                              )}
                            </div>

                            <div
                              className={`form-group ${brideFormData.brideProposedName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Bride Proposed Name
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="brideProposedName"
                                value={brideFormData.brideProposedName || ""}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                          </div>

                          {/* Family Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${brideFormData.fatherName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Father's Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="fatherName"
                                value={brideFormData.fatherName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.fatherName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.fatherName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.motherName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Mother's Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="motherName"
                                value={brideFormData.motherName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.motherName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {brideValidationErrors.motherName}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Previous Marriage Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div className="switch-container">
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  name="wasPreviouslyMarried"
                                  checked={brideFormData.wasPreviouslyMarried}
                                  onChange={handleBrideFormChange}
                                  disabled={isFinalizePage}
                                />
                                <span className="switch-slider"></span>
                              </label>
                              <span className="switch-label">
                                Was Previously Married
                              </span>
                            </div>
                            {brideFormData.wasPreviouslyMarried && (
                              <>
                                <div className="form-group has-value">
                                  <label className="form-label">
                                    Previous Marriage Date
                                  </label>
                                  <input
                                    type="date"
                                    className="form-input"
                                    name="previousMarriageDate"
                                    value={brideFormData.previousMarriageDate}
                                    onChange={handleBrideFormChange}
                                    max={getMaxDateFor18Plus()}
                                    readOnly={isFinalizePage}
                                  />
                                </div>
                                <div
                                  className={`form-group ${brideFormData.previousSpouseName ? "has-value" : ""}`}
                                >
                                  <label className="form-label">
                                    Previous Spouse Name
                                  </label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    name="previousSpouseName"
                                    value={brideFormData.previousSpouseName}
                                    onChange={handleBrideFormChange}
                                    placeholder=" "
                                    readOnly={isFinalizePage}
                                  />
                                </div>
                              </>
                            )}
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {brideApplicant && (
                                <button
                                  type="button"
                                  onClick={handleDeleteBride}
                                  disabled={brideLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: brideLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: brideLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Bride
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={brideLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: brideLoading ? "#ccc" : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: brideLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {brideLoading
                                  ? "Saving..."
                                  : brideApplicant
                                    ? "Update Bride"
                                    : "Save Bride"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Officiator's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setOfficiatorAccordionOpen(!officiatorAccordionOpen)
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Officiator's Detail</span>
                      <span>{officiatorAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {officiatorAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleOfficiatorSubmit}>
                          {officiatorError && (
                            <div
                              style={{
                                padding: "0.75rem",
                                marginBottom: "1rem",
                                background: "#fee",
                                color: "#c00",
                                borderRadius: "4px",
                                border: "1px solid #fcc",
                              }}
                            >
                              {officiatorError}
                            </div>
                          )}

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${officiatorFormData.officiatorName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Officiator Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="officiatorName"
                                value={officiatorFormData.officiatorName}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.officiatorName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {officiatorValidationErrors.officiatorName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${officiatorFormData.officiatorContact ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Officiator Contact *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="officiatorContact"
                                value={officiatorFormData.officiatorContact}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.officiatorContact && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {officiatorValidationErrors.officiatorContact}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${officiatorFormData.witnessName ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Witness Name *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="witnessName"
                                value={officiatorFormData.witnessName}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.witnessName && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {officiatorValidationErrors.witnessName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${officiatorFormData.witnessContact ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                Witness Contact *
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="witnessContact"
                                value={officiatorFormData.witnessContact}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.witnessContact && (
                                <div
                                  style={{
                                    color: "#c00",
                                    fontSize: "0.875rem",
                                    marginTop: "0.25rem",
                                  }}
                                >
                                  {officiatorValidationErrors.witnessContact}
                                </div>
                              )}
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {officiator && (
                                <button
                                  type="button"
                                  onClick={handleDeleteOfficiator}
                                  disabled={officiatorLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: officiatorLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: officiatorLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Officiator
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={officiatorLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: officiatorLoading
                                    ? "#ccc"
                                    : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: officiatorLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {officiatorLoading
                                  ? "Saving..."
                                  : officiator
                                    ? "Update Officiator"
                                    : "Save Officiator"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Attachments Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setAttachmentsAccordionOpen(!attachmentsAccordionOpen)
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Attachments</span>
                      <span>{attachmentsAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {attachmentsAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        {showDocumentScanModal && application && (
                          <DocumentScan
                            application={application}
                            onClose={() => setShowDocumentScanModal(false)}
                            onScanSuccess={() => {
                              fetchData();
                              fetchAttachments();
                            }}
                          />
                        )}
                        {!showDocumentScanModal && (
                          <>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "1rem",
                              }}
                            >
                              <h4
                                style={{
                                  margin: 0,
                                  fontSize: "0.95rem",
                                  fontWeight: "600",
                                  color: "#374151",
                                }}
                              >
                                Uploaded Documents ({attachments.length})
                              </h4>
                              {!isFinalizePage && (
                                <button
                                  className="btn btn--primary"
                                  onClick={() => setShowDocumentScanModal(true)}
                                  style={{
                                    background: "#28a745",
                                    color: "#fff",
                                    border: "none",
                                    padding: "0.5rem 1rem",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.875rem",
                                    fontWeight: "500",
                                  }}
                                >
                                  <span style={{ marginRight: "0.5rem" }}>
                                    ➕
                                  </span>
                                  Add Document
                                </button>
                              )}
                            </div>

                            {attachmentsLoading ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "2rem",
                                  color: "#6b7280",
                                }}
                              >
                                Loading attachments...
                              </div>
                            ) : attachments.length > 0 ? (
                              <div style={{ overflowX: "auto" }}>
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        background: "#f8f9fa",
                                        borderBottom: "2px solid #dee2e6",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "left",
                                          fontWeight: "600",
                                          color: "#374151",
                                        }}
                                      >
                                        Document Type
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "left",
                                          fontWeight: "600",
                                          color: "#374151",
                                        }}
                                      >
                                        Description
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "center",
                                          fontWeight: "600",
                                          color: "#374151",
                                          width: "100px",
                                        }}
                                      >
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {attachments.map((attachment) => (
                                      <React.Fragment key={attachment.id}>
                                        <tr
                                          style={{
                                            borderBottom:
                                              viewingAttachmentId ===
                                              attachment.id
                                                ? "none"
                                                : "1px solid #dee2e6",
                                          }}
                                        >
                                          <td style={{ padding: "0.75rem" }}>
                                            {attachment.documentTypeName || "—"}
                                          </td>
                                          <td style={{ padding: "0.75rem" }}>
                                            {attachment.description || "—"}
                                          </td>
                                          <td
                                            style={{
                                              padding: "0.75rem",
                                              textAlign: "center",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "0.5rem",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <button
                                                onClick={() => {
                                                  const newId =
                                                    viewingAttachmentId ===
                                                    attachment.id
                                                      ? null
                                                      : attachment.id;
                                                  setViewingAttachmentId(newId);
                                                }}
                                                title={
                                                  viewingAttachmentId ===
                                                  attachment.id
                                                    ? "Hide document"
                                                    : "View document"
                                                }
                                                style={{
                                                  background: "transparent",
                                                  border: "none",
                                                  cursor: "pointer",
                                                  fontSize: "1.25rem",
                                                  padding: "0.25rem",
                                                  color:
                                                    viewingAttachmentId ===
                                                    attachment.id
                                                      ? "#10b981"
                                                      : "#3b82f6",
                                                }}
                                              >
                                                {viewingAttachmentId ===
                                                attachment.id
                                                  ? "👁️‍🗨️"
                                                  : "👁️"}
                                              </button>
                                              {!isFinalizePage && (
                                                <button
                                                  onClick={() =>
                                                    handleDeleteAttachment(
                                                      attachment,
                                                    )
                                                  }
                                                  title="Delete attachment"
                                                  style={{
                                                    background: "transparent",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    fontSize: "1.25rem",
                                                    padding: "0.25rem",
                                                    color: "#ef4444",
                                                  }}
                                                >
                                                  🗑️
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                        {viewingAttachmentId ===
                                          attachment.id && (
                                          <tr>
                                            <td
                                              colSpan={3}
                                              style={{
                                                padding: 0,
                                                borderBottom:
                                                  "1px solid #dee2e6",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  background: "#f8f9fa",
                                                  padding: "1rem",
                                                  borderTop:
                                                    "2px solid #3b82f6",
                                                }}
                                              >
                                                {attachment.description && (
                                                  <div
                                                    style={{
                                                      marginBottom: "0.75rem",
                                                      padding: "0.5rem",
                                                      background: "#fff",
                                                      borderRadius: "4px",
                                                      fontSize: "0.875rem",
                                                      color: "#6b7280",
                                                    }}
                                                  >
                                                    <strong>
                                                      Description:
                                                    </strong>{" "}
                                                    {attachment.description}
                                                  </div>
                                                )}
                                                {(() => {
                                                  // Validate attachment has documentPath
                                                  if (
                                                    !attachment.documentPath
                                                  ) {
                                                    return (
                                                      <div
                                                        style={{
                                                          padding: "1rem",
                                                          background: "#fee",
                                                          color: "#c00",
                                                          borderRadius: "4px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <strong>Error:</strong>{" "}
                                                        Document file path is
                                                        missing
                                                      </div>
                                                    );
                                                  }

                                                  const baseUrl =
                                                    BASE_URL.replace(
                                                      "/api",
                                                      "",
                                                    );
                                                  const documentPath =
                                                    attachment.documentPath.startsWith(
                                                      "/",
                                                    )
                                                      ? attachment.documentPath.substring(
                                                          1,
                                                        )
                                                      : attachment.documentPath;
                                                  const fullUrl = `${baseUrl}/${documentPath}`;

                                                  return (
                                                    <div>
                                                      <div
                                                        style={{
                                                          marginBottom:
                                                            "0.5rem",
                                                          padding: "0.5rem",
                                                          background: "#fff",
                                                          borderRadius: "4px",
                                                          fontSize: "0.75rem",
                                                          color: "#6b7280",
                                                          wordBreak:
                                                            "break-all",
                                                        }}
                                                      >
                                                        <strong>
                                                          Document URL:
                                                        </strong>{" "}
                                                        {fullUrl}
                                                      </div>
                                                      <iframe
                                                        key={attachment.id}
                                                        src={
                                                          getFullPath(
                                                            fullUrl,
                                                          ) || undefined
                                                        }
                                                        title={
                                                          attachment.documentTypeName ||
                                                          "Document"
                                                        }
                                                        style={{
                                                          width: "100%",
                                                          height: "600px",
                                                          border:
                                                            "1px solid #dee2e6",
                                                          borderRadius: "4px",
                                                          background: "#fff",
                                                        }}
                                                      />
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "2rem",
                                  background: "#f8f9fa",
                                  borderRadius: "4px",
                                  color: "#6b7280",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "2rem",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  📁
                                </div>
                                <div
                                  style={{
                                    fontWeight: "500",
                                    marginBottom: "0.25rem",
                                  }}
                                >
                                  No documents uploaded yet
                                </div>
                                <div style={{ fontSize: "0.875rem" }}>
                                  Click "Add Document" to upload your first
                                  document
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Column 2: Action Buttons */}
              <div className="user-detail-actions">
                <button
                  className="user-detail-back user-detail-back--desktop"
                  onClick={() => navigate(getBackPath())}
                >
                  ← Back to Applications
                </button>

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Print,
                  ) &&
                  areAllSignaturesPresent() && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={() =>
                        navigate(`/marriages/finalize-print/${id}`)
                      }
                      title="Print certificate"
                      style={{
                        background: "#6f42c1",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">🖨️</span>
                      Print Certificate
                    </button>
                  )}

                {!isPrintPage &&
                  !signedCertificate &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.View,
                  ) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--success"
                      onClick={() => setShowSignedCertModal(true)}
                      title="Receive signed certificate"
                      style={{
                        background: "#28a745",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">📄</span>
                      Receive Signed Cert
                    </button>
                  )}

                {!isPrintPage &&
                  signedCertificate &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.View,
                  ) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={() => setShowSignatureExtractionModal(true)}
                      title="Extract signatures from certificate"
                      style={{
                        background: "#007bff",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">✍️</span>
                      Extract Signatures
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Apply,
                  ) &&
                  !hasExistingPayment && (
                    <div style={{ position: "relative" }}>
                      <button
                        className="user-detail-action-btn user-detail-action-btn--warning"
                        onClick={() =>
                          setPaymentDropdownOpen(!paymentDropdownOpen)
                        }
                        title="Make payment"
                        style={{
                          background: "#ffc107",
                          color: "#000",
                        }}
                      >
                        <span className="action-icon">💳</span>
                        Make Payment
                        <span style={{ marginLeft: "0.5rem" }}>▼</span>
                      </button>
                      {paymentDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            marginTop: "0.25rem",
                            background: "#fff",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            zIndex: 1000,
                            overflow: "hidden",
                          }}
                        >
                          <button
                            onClick={() => handlePayment("Manual")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              borderBottom: "1px solid #dee2e6",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>📝</span>
                            Manual Payment
                          </button>
                          <button
                            onClick={() => handlePayment("Card")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              borderBottom: "1px solid #dee2e6",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>💳</span>
                            Card Payment
                          </button>
                          <button
                            onClick={() => handlePayment("Mobile Money")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>📱</span>
                            Mobile Money
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Edit,
                  ) &&
                  isReadyForSubmission &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--info"
                      onClick={handleSubmit}
                      title="Submit application"
                      style={{
                        background: "#17a2b8",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">📋</span>
                      Submit
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--success"
                      onClick={handleApprove}
                      title="Approve application"
                      style={{
                        background: "#28a745",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">✅</span>
                      Approve
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--warning"
                      onClick={handleQuery}
                      title="Query application"
                      style={{
                        background: "#ffc107",
                        color: "#000",
                      }}
                    >
                      <span className="action-icon">❓</span>
                      Query
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--danger"
                      onClick={handleReject}
                      title="Reject application"
                      style={{
                        background: "#dc3545",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">❌</span>
                      Reject
                    </button>
                  )}
              </div>
            </div>
          )}

          <QueryApplicationForm
            isOpen={showQueryForm}
            onClose={() => setShowQueryForm(false)}
            entityName="CertificateApplications"
            recordId={application?.id}
            workflowId={application?.workflowId}
            statusId={application?.statusId}
            onSubmit={handleSubmitQuery}
          />

          {/* Edit Application Modal */}
          {showEditModal && application && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Edit Application</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowEditModal(false);
                      resetEditForm();
                    }}
                    disabled={editLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleUpdate}>
                  {editError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{editError}</span>
                      <button
                        type="button"
                        onClick={() => setEditError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div className="form-grid-responsive">
                    <div className="form-group has-value">
                      <input
                        type="text"
                        className="form-input"
                        value={application.formNo || ""}
                        disabled
                        placeholder=" "
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      />
                      <label className="form-label">Form No</label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.category || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            category: e.target.value,
                          })
                        }
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      >
                        <option value={ApplicationCategory.Marriage}>
                          {ApplicationCategory.Marriage}
                        </option>
                        <option value={ApplicationCategory.Divorce}>
                          {ApplicationCategory.Divorce}
                        </option>
                      </select>
                      <label className="form-label">Category</label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.countyId || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            countyId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value="">Select County</option>
                        {counties.map((county) => (
                          <option key={county.id} value={county.id}>
                            {county.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        County <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${
                        editFormData.placeOfWedding ? "has-value" : ""
                      }`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.placeOfWedding || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            placeOfWedding: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Place of Marriage <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <input
                        type="date"
                        className="form-input"
                        value={editFormData.dateOfWedding || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            dateOfWedding: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Date of Marriage <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${
                        editFormData.email ? "has-value" : ""
                      }`}
                    >
                      <input
                        type="email"
                        className="form-input"
                        value={editFormData.email || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            email: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">Email</label>
                    </div>

                    <div className="form-group has-value">
                      <input
                        type="date"
                        className="form-input"
                        value={editFormData.dateOfCertification || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            dateOfCertification: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">
                        Date of Certification
                      </label>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowEditModal(false);
                        resetEditForm();
                      }}
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Updating...
                        </>
                      ) : (
                        "Update Application"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Receive Signed Certificate Modal */}
          {showSignedCertModal && application && (
            <DocumentScan
              application={application}
              title="Scan or Upload Certificate"
              hideDocumentType={true}
              hideDescription={true}
              uploadEndpoint="/SignedCertificates"
              onClose={() => setShowSignedCertModal(false)}
              onScanSuccess={() => {
                setShowSignedCertModal(false);
                fetchData();
                fetchAttachments();
                fetchSignedCertificate();
                showSuccessToast("Signed certificate received successfully");
              }}
            />
          )}

          {/* Manual Payment Modal */}
          {showManualPaymentModal && application && (
            <ManualPayment
              application={application}
              onClose={() => setShowManualPaymentModal(false)}
              onPaymentSuccess={() => fetchData()}
            />
          )}

          {/* Signature Extraction Modal */}
          {showSignatureExtractionModal && application && signedCertificate && (
            <SignatureExtraction
              application={application}
              documentPath={signedCertificate.documentPath}
              onClose={() => setShowSignatureExtractionModal(false)}
            />
          )}

          {/* Card Payment Modal */}
          {showCardPaymentModal && application && (
            <CardPayment
              application={application}
              onClose={() => setShowCardPaymentModal(false)}
              onPaymentSuccess={() => fetchData()}
            />
          )}
        </div>
      </main>
      {!isPrintPage && <Footer />}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .dashboard-body {
            margin: 0 !important;
            padding: 0 !important;
          }
          .main-content {
            margin: 0 !important;
            padding: 0 !important;
          }
          .user-detail-page {
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default MarriageCertDetail;
